import {ProductResponseDTO} from "@shared/DTO";
import {SpecsItem} from "@/scripts/modules/ab-data-sheet/types/specifications.types";
import {NAMES} from "@/scripts/components/ab-data-sheet/ab-data-sheet.constants";
import {createDomElementHandler} from "@/scripts/utils/dom.utils";

export const buildDataSheetTable = (
  products: ProductResponseDTO[],
  specs: SpecsItem[],
  createPurchaseCta: (product: ProductResponseDTO) => HTMLElement,
): HTMLElement => {
  const el = createDomElementHandler(
    'div',
    "ab-data-sheet__table-wrapper"
  );
  el.innerHTML = `      
    <table class="ab-data-sheet__table">
      ${buildTableHeader(products)}
      <tbody class="ab-data-sheet__body">
        ${buildSpecsRows(specs, products)}
        ${buildImageRow(products)}
        ${buildReviewRow(products)}
      </tbody>
    </table>      
  `;
  const footer = buildFooter(products, createPurchaseCta);
  if (footer) el.querySelector('table').appendChild(footer);
  return el;
}

const buildTableHeader = (products: ProductResponseDTO[]): string => {
  if(products.length <= 1) return '';
  const cells = products.map(product => {
    let removeButtonHtml = `
      <span 
        class="${NAMES.CSS_CLASSES.TRIGGER_REMOVE_ITEM}"
        data-product-id="${product._id}"
      >
      rimuovi       
      </span>
    `;
    return `
    <th class="ab-data-sheet__cell">
      ${product.productData.names[0]}
      ${removeButtonHtml}
    </th>
  `
  }).join('');

  return `
    <thead class="ab-data-sheet__heading">
      <tr class="ab-data-sheet__row ab-data-sheet__row--heading">
        <th class="ab-data-sheet__cell ab-data-sheet__cell--label"></th>
        ${cells}
      </tr>
    </thead>
  `;
}

const buildSpecsRows = (specs: SpecsItem[], products: ProductResponseDTO[]): string => {
  return specs.map(spec => {
    if (!spec) return '';
    const cells = products.map(product => {
      const value = spec.values.find(v => v.id === product._id);
      const isBest = spec.bestProducts?.includes(product._id);
      const bestClass = isBest ? ' ab-data-sheet__cell--best' : '';
      const productClass = ` ab-data-sheet__cell--${product.productData.id}`;
      return `
        <td class="ab-data-sheet__cell ab-data-sheet__cell--value${productClass}${bestClass}">
          ${value?.html || ''}
        </td>
      `;
    }).join('');
    return `
      <tr class="ab-data-sheet__row">
        <td class="ab-data-sheet__cell ab-data-sheet__cell--label">${spec.label}</td>
        ${cells}
      </tr>
    `;
  }).join('');
}

const buildImageRow = (
  products: ProductResponseDTO[]
): string => {
  const cells = products.map(product => {
    const image = product.productData.images?.[0];
    const size = image?.image?.sizes?.find(s => s.width === 400) || image?.image?.sizes?.[0];
    const imageUrl = size?.url;
    const altText = image?.altTitle || product.productData.names[0];
    const productClass = ` ab-data-sheet__cell--${product.productData.id}`;
    const imgBasePath = (import.meta as any).env?.VITE_FRONTEND_BASE_URL;
    const content = imageUrl
      ? `<div class="ab-data-sheet__product-image-wrapper"><img class="ab-data-sheet__product-image" src="${imgBasePath}${imageUrl}" alt="${altText}"></div>`
      : '';
    return `
      <td class="ab-data-sheet__cell ab-data-sheet__cell--value${productClass}">
        ${content}
      </td>
    `;
  }).join('');
  return `
    <tr class="ab-data-sheet__row">
      <td class="ab-data-sheet__cell ab-data-sheet__cell--label"></td>
      ${cells}
    </tr>
  `;
}

const buildReviewRow = (
  products: ProductResponseDTO[]
): string => {
  const noReviews = products.every(product => !product.productData?.productReviewUrl);
  if (noReviews) return '';
  const cells = products.map(product => {
    const reviewUrl = product.productData.productReviewUrl;
    const productClass = ` ab-data-sheet__cell--${product.productData.id}`;
    const content = reviewUrl
      ? `<a class="ab-data-sheet__product-review-link" href="${reviewUrl}">Vedi recensione</a>`
      : '';
    return `
      <td class="ab-data-sheet__cell ab-data-sheet__cell--value${productClass}">
        ${content}
      </td>
    `;
  }).join('');

  return `
    <tr class="ab-data-sheet__row">
      <td class="ab-data-sheet__cell ab-data-sheet__cell--label"></td>
      ${cells}
    </tr>
  `;
}

const buildFooter = (
  products: ProductResponseDTO[],
  createPurchaseCta: (product: ProductResponseDTO) => HTMLElement,
): HTMLElement | undefined => {
  const noLinks = products.every(product =>
    !product.affiliate?.some(affiliate => affiliate.program === 'amazon' && affiliate.url?.length),
  );
  if (noLinks) return;
  const footer = createDomElementHandler('tfoot', 'ab-data-sheet__footer');
  const row = createDomElementHandler('tr', ['ab-data-sheet__row', 'ab-data-sheet__row--footer']);
  row.appendChild(createDomElementHandler('td', ['ab-data-sheet__cell', 'ab-data-sheet__cell--label'], undefined, 'Acquista'));
  products.forEach(product => {
    const cell = createDomElementHandler('td', 'ab-data-sheet__cell');
    cell.appendChild(createPurchaseCta(product));
    row.appendChild(cell);
  });
  footer.appendChild(row);
  return footer;
};
